import { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import AddRounded from '@mui/icons-material/AddRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import ImageRounded from '@mui/icons-material/ImageRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import { uploadImage } from '../../services/uploadsApi';

// Compact image-upload control: Avatar preview + small upload-icon button.
// Clicking either opens a file picker; the chosen file is uploaded to GCS
// and the returned URL is reported via onChange.
const InlineImageUpload = ({ value, onChange, folder = 'product-extra-info', size = 36 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const url = await uploadImage(file, folder);
      onChange(url);
    } catch (err) {
      console.error('Image upload failed:', err);
    } finally {
      setUploading(false);
    }
  };
  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFile} />
      <Avatar
        variant="rounded"
        src={value || ''}
        onClick={() => !uploading && inputRef.current?.click()}
        sx={{ width: size, height: size, bgcolor: 'grey.100', cursor: uploading ? 'wait' : 'pointer' }}
      >
        {uploading
          ? <CircularProgress size={size * 0.5} />
          : <ImageRounded sx={{ fontSize: size * 0.45, color: 'grey.400' }} />}
      </Avatar>
    </>
  );
};

const genId = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

// Inner UI for an item entry (title + subitems). Used by both the legacy
// "right" variant and the new "middle" entry list.
const ItemBody = ({ item, onChange }) => {
  const subitems = item.subitems || [];
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const updateSub = (sIdx, patch) =>
    onChange({ ...item, subitems: subitems.map((s, i) => (i === sIdx ? { ...s, ...patch } : s)) });
  const addSub = () =>
    onChange({ ...item, subitems: [...subitems, { image: '', value: '' }] });
  const removeSub = (sIdx) =>
    onChange({ ...item, subitems: subitems.filter((_, i) => i !== sIdx) });

  const dropSub = (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); setHoverIndex(null); return; }
    const next = [...subitems];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange({ ...item, subitems: next });
    setDragIndex(null);
    setHoverIndex(null);
  };

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <TextField
          size="small"
          placeholder="Item title"
          value={item.title || ''}
          onChange={(e) => onChange({ ...item, title: e.target.value })}
          fullWidth
          InputProps={{ sx: { borderRadius: 2, fontWeight: 600 } }}
        />
      </Box>

      {subitems.map((sub, sIdx) => (
        <Box
          key={sIdx}
          onDragOver={(e) => { e.preventDefault(); if (hoverIndex !== sIdx) setHoverIndex(sIdx); }}
          onDrop={(e) => { e.preventDefault(); dropSub(sIdx); }}
          sx={{
            display: 'flex', gap: 1, alignItems: 'center', mb: 1, ml: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: hoverIndex === sIdx && dragIndex !== null && dragIndex !== sIdx ? 'primary.main' : 'transparent',
            opacity: dragIndex === sIdx ? 0.5 : 1,
          }}
        >
          <Box
            draggable
            onDragStart={(e) => { setDragIndex(sIdx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(sIdx)); } catch { /* Safari */ } }}
            onDragEnd={() => { setDragIndex(null); setHoverIndex(null); }}
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'grab', '&:active': { cursor: 'grabbing' } }}
            title="Drag to reorder"
          >
            <DragIndicatorRounded sx={{ fontSize: 18 }} />
          </Box>
          <InlineImageUpload
            value={sub.image}
            onChange={(url) => updateSub(sIdx, { image: url })}
            folder="product-extra-info"
            size={36}
          />
          {sub.image && (
            <IconButton size="small" onClick={() => updateSub(sIdx, { image: '' })} title="Clear image" sx={{ p: 0.25 }}>
              <CloseRounded sx={{ fontSize: 14, color: 'text.secondary' }} />
            </IconButton>
          )}
          <TextField
            size="small"
            placeholder="Value / label"
            value={sub.value || ''}
            onChange={(e) => updateSub(sIdx, { value: e.target.value })}
            sx={{ flex: 1 }}
            InputProps={{ sx: { borderRadius: 2, fontSize: '0.85rem' } }}
          />
          <IconButton
            size="small"
            onClick={() => removeSub(sIdx)}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
          >
            <CloseRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        variant="text"
        startIcon={<AddRounded />}
        onClick={addSub}
        sx={{ textTransform: 'none', mt: 0.5, ml: 1 }}
      >
        Add subitem
      </Button>
    </>
  );
};

// "Right" variant: list of items (title + subitems). Items and their subitems
// are reorderable via native HTML5 drag-and-drop (same pattern as Middle).
const RightEditor = ({ data, onChange }) => {
  const items = data.items || [];
  const updateItems = (next) => onChange({ ...data, items: next });
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const handleDrop = (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) { setDragIndex(null); setHoverIndex(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    updateItems(next);
    setDragIndex(null);
    setHoverIndex(null);
  };

  return (
    <Box>
      {items.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No items yet.
        </Typography>
      )}

      {items.map((item, idx) => {
        const isHover = hoverIndex === idx && dragIndex !== null && dragIndex !== idx;
        return (
        <Box
          key={idx}
          onDragOver={(e) => { e.preventDefault(); if (hoverIndex !== idx) setHoverIndex(idx); }}
          onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
          sx={{
            mb: 2, border: '1px solid', borderColor: isHover ? 'primary.main' : 'divider',
            borderRadius: 2, p: 2, opacity: dragIndex === idx ? 0.5 : 1,
            transition: 'border-color 0.12s, opacity 0.12s',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1.5 }}>
            <Box
              draggable
              onDragStart={(e) => { setDragIndex(idx); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* Safari */ } }}
              onDragEnd={() => { setDragIndex(null); setHoverIndex(null); }}
              sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'grab', pt: 0.5, '&:active': { cursor: 'grabbing' } }}
              title="Drag to reorder"
            >
              <DragIndicatorRounded fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <ItemBody
                item={item}
                onChange={(next) => updateItems(items.map((it, i) => (i === idx ? next : it)))}
              />
            </Box>
            <IconButton
              onClick={() => updateItems(items.filter((_, i) => i !== idx))}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, alignSelf: 'flex-start' }}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        );
      })}

      <Button
        size="small"
        variant="outlined"
        startIcon={<AddRounded />}
        onClick={() => updateItems([...items, { title: '', subitems: [{ image: '', value: '' }] }])}
        sx={{ borderRadius: 99, textTransform: 'none' }}
      >
        Add item
      </Button>
    </Box>
  );
};

// "Middle" variant: a single sortable list mixing banner and item entries.
// Entries shape: [{ id, type: 'banner', image }, { id, type: 'item', title, subitems }]
// Reordered with native HTML5 drag-and-drop; no extra deps.
const MiddleEditor = ({ data, onChange }) => {
  const entries = Array.isArray(data.entries) ? data.entries : [];
  const [dragIndex, setDragIndex] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const setEntries = (next) => onChange({ ...data, entries: next });
  const updateEntry = (idx, next) => setEntries(entries.map((e, i) => (i === idx ? next : e)));
  const removeEntry = (idx) => setEntries(entries.filter((_, i) => i !== idx));

  const addItem = () =>
    setEntries([...entries, { id: genId(), type: 'item', title: '', subitems: [{ image: '', value: '' }] }]);
  const addBanner = () =>
    setEntries([...entries, { id: genId(), type: 'banner', image: '' }]);

  const handleDrop = (toIndex) => {
    if (dragIndex === null || dragIndex === toIndex) {
      setDragIndex(null);
      setHoverIndex(null);
      return;
    }
    const next = [...entries];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(toIndex, 0, moved);
    setEntries(next);
    setDragIndex(null);
    setHoverIndex(null);
  };

  return (
    <Box>
      {entries.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No entries yet. Add an item or a banner below.
        </Typography>
      )}

      {entries.map((entry, idx) => {
        const isDragging = dragIndex === idx;
        const isHover = hoverIndex === idx && dragIndex !== null && dragIndex !== idx;
        return (
          <Box
            key={entry.id || idx}
            draggable
            onDragStart={(e) => {
              setDragIndex(idx);
              e.dataTransfer.effectAllowed = 'move';
              try { e.dataTransfer.setData('text/plain', String(idx)); } catch { /* Safari quirk */ }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (hoverIndex !== idx) setHoverIndex(idx);
            }}
            onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
            onDragEnd={() => { setDragIndex(null); setHoverIndex(null); }}
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: isHover ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 2,
              backgroundColor: 'background.paper',
              opacity: isDragging ? 0.5 : 1,
              transition: 'border-color 0.12s, opacity 0.12s',
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                cursor: 'grab',
                pt: 0.5,
                '&:active': { cursor: 'grabbing' },
              }}
              title="Drag to reorder"
            >
              <DragIndicatorRounded fontSize="small" />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              {entry.type === 'banner' ? (
                <Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', fontWeight: 700 }}
                  >
                    Banner
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    <InlineImageUpload
                      value={entry.image}
                      onChange={(url) => updateEntry(idx, { ...entry, image: url })}
                      folder="product-extra-info"
                      size={56}
                    />
                    {entry.image ? (
                      <Button size="small" onClick={() => updateEntry(idx, { ...entry, image: '' })}>
                        Clear
                      </Button>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        Click the box to upload
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <ItemBody
                  item={entry}
                  onChange={(next) => updateEntry(idx, { ...next, id: entry.id, type: 'item' })}
                />
              )}
            </Box>

            <IconButton
              onClick={() => removeEntry(idx)}
              sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
              title={entry.type === 'banner' ? 'Remove banner' : 'Remove item'}
            >
              <CloseRounded fontSize="small" />
            </IconButton>
          </Box>
        );
      })}

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={addItem}
          sx={{ borderRadius: 99, textTransform: 'none' }}
        >
          Add item
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddRounded />}
          onClick={addBanner}
          sx={{ borderRadius: 99, textTransform: 'none' }}
        >
          Add banner
        </Button>
      </Box>
    </Box>
  );
};

// Shared editor for "extra information" sections on the product page.
//   - "right": list of items with title + subitems (legacy shape, unchanged).
//   - "middle": sortable list of entries that can be banner OR item.
// The "middle" editor reads/writes the new `{ entries: [...] }` shape; the
// parent is expected to feed legacy shapes through normalizeMiddleToEntries
// before passing them in.
const ExtraInfoEditor = ({ value, onChange, variant = 'right' }) => {
  const data = value || {};
  if (variant === 'middle') {
    return <MiddleEditor data={data} onChange={onChange} />;
  }
  return <RightEditor data={data} onChange={onChange} />;
};

// Convert a legacy `{ banner, items }` middle into the new entries shape.
// Idempotent: if `entries` is already present, returns it as-is (but
// stamps stable ids for React keys / drag identity).
export const normalizeMiddleToEntries = (middle) => {
  if (!middle || typeof middle !== 'object') return { entries: [] };
  if (Array.isArray(middle.entries)) {
    return {
      entries: middle.entries.map((e) => ({ ...e, id: e?.id || genId() })),
    };
  }
  const entries = [];
  if (middle.banner) {
    entries.push({ id: genId(), type: 'banner', image: middle.banner });
  }
  for (const item of (middle.items || [])) {
    entries.push({
      id: genId(),
      type: 'item',
      title: item.title || '',
      subitems: item.subitems || [],
    });
  }
  return { entries };
};

export default ExtraInfoEditor;
